import type {
  ChatMessage as ChatMessageType,
  ReasoningStep,
  ConversationHistoryMessage,
  ToolMetaData,
  ToolInvocationData,
} from '@/types/chat';
import type React from 'react';

const ID_KEY_RE = /(?:_[Ii][Dd]s?|[a-z]Ids?|^[Ii][Dd]s?)$/;

/**
 * `ChatMessage.content` is `string | ReactNode`; anything the LLM or the
 * clipboard consumes needs the plain-string half of that union.
 */
export const toPlainText = (content: React.ReactNode): string =>
  typeof content === 'string' ? content : '';

/** Parsed LangChain message shape from agent-server SSE streams. */
export type StreamLangChainMessage = {
  id?: string[];
  kwargs?: Record<string, unknown>;
  type?: string;
  content?: string;
};

/** LangChain messages in SSE may be objects or JSON strings depending on serialization. */
export const normalizeStreamMessages = (
  messages: unknown[],
): StreamLangChainMessage[] =>
  messages.map(msg => {
    if (typeof msg === 'string') {
      try {
        return JSON.parse(msg) as StreamLangChainMessage;
      } catch {
        return { content: msg };
      }
    }
    return msg as StreamLangChainMessage;
  });

const unwrapToolPayload = (raw: unknown): Record<string, unknown> | null => {
  if (raw == null) return null;
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  if (
    Object.keys(obj).length === 1 &&
    obj.result != null &&
    typeof obj.result === 'object' &&
    !Array.isArray(obj.result)
  ) {
    return obj.result as Record<string, unknown>;
  }
  return obj;
};

/** Last successful tool JSON `message` field — fallback when the agent omits final text. */
export const extractLastToolResultMessage = (messages: any[]): string => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType !== 'ToolMessage' || msg.kwargs?.status === 'error') {
      continue;
    }
    const payload = unwrapToolPayload(msg.kwargs?.content);
    const message = payload?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }
  return '';
};

const collectIds = (
  node: unknown,
  into: Record<string, any>,
  prefix = '',
): void => {
  if (node === null || typeof node !== 'object' || Array.isArray(node)) return;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (ID_KEY_RE.test(key)) {
      if (
        value != null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        into[fullKey] = value;
      }
    } else if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      collectIds(value, into, fullKey);
    }
  }
};

/**
 * Build the metadata-stub JSON string that lives in `tool_result.content` for
 * a single tool invocation. Carries only what later turns need to issue
 * follow-up calls (status, ref_id, ids); the original response payload is
 * dropped.
 */
export const buildToolMetadataContent = (
  toolName: string,
  status: string,
  responsePayload: string | null,
  errorMessage: string | null,
): string => {
  const stub: Record<string, any> = { tool: toolName };

  if (status !== 'success') {
    stub.status = 'error';
    if (errorMessage) stub.error = errorMessage;
    return JSON.stringify(stub);
  }

  stub.status = 'success';
  if (responsePayload) {
    try {
      const parsed = JSON.parse(responsePayload);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const refCandidate = (parsed as Record<string, unknown>).ref_id;
        if (typeof refCandidate === 'string' && refCandidate.length > 0) {
          stub.ref_id = refCandidate;
        }
        const ids: Record<string, any> = {};
        collectIds(parsed, ids);
        delete ids.ref_id;
        if (Object.keys(ids).length > 0) stub.ids = ids;
      }
    } catch {
      // Non-JSON response — emit stub without ids.
    }
  }

  return JSON.stringify(stub);
};

/**
 * Convert the per-turn tool invocation list (from streaming or from a
 * conversation reload) into compact ToolMetaData records that the next
 * request's `formatConversationHistory` will replay into LLM context.
 */
export const buildToolMetaData = (
  invocations: ToolInvocationData[],
): ToolMetaData[] => {
  return invocations.map(inv => {
    let args: Record<string, any> = {};
    if (inv.tool_request_payload) {
      try {
        const parsed = JSON.parse(inv.tool_request_payload);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          args = parsed as Record<string, any>;
        }
      } catch {
        /* leave args empty */
      }
    }
    return {
      tool_name: inv.tool_name,
      tool_call_id: inv.tool_call_id || `tool-${inv.invocation_id}`,
      args,
      content: buildToolMetadataContent(
        inv.tool_name,
        inv.status,
        inv.tool_response_payload,
        inv.error_message,
      ),
    };
  });
};

/**
 * Extract ToolMetaData directly from the raw streamed LangChain messages
 * by pairing each AIMessage tool_call with its later ToolMessage result.
 */
export const extractToolMetaDataFromStream = (
  allMessages: any[],
): ToolMetaData[] => {
  const aiToolCalls = new Map<
    string,
    { name: string; args: Record<string, any> }
  >();
  const out: ToolMetaData[] = [];

  for (const msg of allMessages) {
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType === 'AIMessage' && Array.isArray(msg.kwargs?.tool_calls)) {
      for (const tc of msg.kwargs.tool_calls) {
        if (tc?.id) {
          aiToolCalls.set(tc.id, { name: tc.name, args: tc.args || {} });
        }
      }
    } else if (msgType === 'ToolMessage' && msg.kwargs?.tool_call_id) {
      const callId = msg.kwargs.tool_call_id;
      const matched = aiToolCalls.get(callId);
      const toolName = matched?.name || msg.kwargs.name || 'unknown';
      const isError = msg.kwargs.status === 'error';
      const rawContent = msg.kwargs.content;
      const responseText =
        typeof rawContent === 'string'
          ? rawContent
          : rawContent != null
          ? JSON.stringify(rawContent)
          : null;
      const errorMessage =
        isError && typeof rawContent === 'string' ? rawContent : null;
      out.push({
        tool_name: toolName,
        tool_call_id: callId,
        args: matched?.args || {},
        content: buildToolMetadataContent(
          toolName,
          isError ? 'failed' : 'success',
          responseText,
          errorMessage,
        ),
      });
    }
  }

  return out;
};

export const updateBlocks = (
  existingBlocks: any[] | undefined,
  content: string,
  extras?: ChatMessageType['extras'],
) => {
  let blocks = [...(existingBlocks || [])];

  // Add files block first if it doesn't exist yet
  const filesData = extras?.files;
  if (filesData?.length && !blocks.some(b => b.type === 'files')) {
    blocks.push({ type: 'files', data: filesData });
  }

  // Add MCP-UI resource block if it doesn't exist yet
  const uiResources = (extras as any)?.uiResources;
  if (uiResources?.length && !blocks.some(b => b.type === 'ui')) {
    blocks.push({ type: 'ui', data: uiResources });
  }

  const hasText = content && content.trim().length > 0;
  const textIndex = blocks.findIndex(b => b.type === 'text');

  if (hasText) {
    if (textIndex === -1) {
      blocks.push({ type: 'text', data: content });
    } else {
      blocks[textIndex] = { type: 'text', data: content };
    }
  } else if (textIndex !== -1) {
    blocks = blocks.filter(b => b.type !== 'text');
  }

  return blocks;
};

export const updateReasoningSteps = (
  existingSteps: ReasoningStep[] | undefined,
  newSteps: ReasoningStep[] | undefined,
) => {
  if (!newSteps || newSteps.length === 0) return existingSteps;
  return newSteps;
};

export const extractChartMetadata = (
  messages: ChatMessageType[],
): Array<{ type: string; title: string; messageIndex: number }> => {
  const chartMetadata: Array<{
    type: string;
    title: string;
    messageIndex: number;
  }> = [];
  messages.forEach((msg, idx) => {
    const chartBlocks = msg.blocks?.filter(b => b.type === 'charts');
    if (chartBlocks && chartBlocks.length > 0) {
      chartBlocks.forEach(block => {
        const charts = Array.isArray(block.data) ? block.data : [block.data];
        charts.forEach((chart: any) => {
          chartMetadata.push({
            type: chart?.config?.chart?.type || 'unknown',
            title: chart?.config?.title?.text || 'Untitled',
            messageIndex: idx,
          });
        });
      });
    }
  });
  return chartMetadata;
};

export const formatConversationHistory = (
  messages: ChatMessageType[],
): ConversationHistoryMessage[] => {
  return messages.flatMap(msg => {
    const result: ConversationHistoryMessage[] = [];

    if (msg.role === 'user') {
      result.push({
        type: 'human',
        content: toPlainText(msg.content),
        tool_calls: [],
        tool_call_id: '',
      });
    } else if (msg.role === 'assistant') {
      let content = toPlainText(msg.content);
      const files = msg.extras?.files;
      if (files && Array.isArray(files) && files.length > 0) {
        const fileInfo = files
          .map(
            (f: any, i: number) =>
              `File ${i + 1}:\nFilename: ${f.filename}\nSize: ${
                f.size
              }\nS3 URL: ${f.s3_url}`,
          )
          .join('\n\n');
        content = `${content}\n\nUploaded files:\n${fileInfo}`;
      }

      // Expand a single assistant turn into ai(tool_calls) -> tool... -> ai(text)
      // so the LLM sees a structurally-complete tool_use/tool_result sequence
      // on every replay.
      const toolMetaData = msg.extras?.toolMetaData;
      if (toolMetaData && toolMetaData.length > 0) {
        result.push({
          type: 'ai',
          content: '',
          tool_calls: toolMetaData.map(t => ({
            name: t.tool_name,
            args: t.args,
            id: t.tool_call_id,
            type: 'tool_call',
          })),
          tool_call_id: '',
        });
        for (const t of toolMetaData) {
          result.push({
            type: 'tool',
            content: t.content,
            tool_calls: [],
            tool_call_id: t.tool_call_id,
          });
        }
      }

      // Omit a trailing blank assistant turn — it breaks Bedrock Converse replay.
      if (content.trim()) {
        result.push({
          type: 'ai',
          content,
          tool_calls: [],
          tool_call_id: '',
        });
      }
    }

    return result;
  });
};

/**
 * Extract MCP-UI resources from tool messages.
 *
 * The ToolMessage kwargs contain ``ui_resource_uri`` + ``tool_input``
 * injected by the agent server. The frontend fetches the static template
 * on demand via ``/mcp/resources/read`` and passes data to AppRenderer.
 */
export const processUIResourcesFromToolMessage = (
  messages: any[],
): any[] | undefined => {
  const resources: any[] = [];

  for (const msg of messages) {
    if (typeof msg !== 'object' || !msg.id || !Array.isArray(msg.id)) {
      continue;
    }

    const messageType = msg.id[msg.id.length - 1];
    if (messageType !== 'ToolMessage' || !msg.kwargs) {
      continue;
    }

    if (msg.kwargs.status === 'error') {
      continue;
    }

    const uiResourceUri = msg.kwargs.ui_resource_uri;
    if (uiResourceUri) {
      resources.push({
        uri: uiResourceUri,
        resourceUri: uiResourceUri,
        toolName: msg.kwargs.name || 'mcp-tool',
        toolInput: msg.kwargs.tool_input || {},
        toolResult: msg.kwargs.content,
      });
    }
  }

  return resources.length > 0 ? resources : undefined;
};

/**
 * Strip internal metadata tags (e.g. <tool_result_ids>) that the LLM may
 * echo back from conversation history. These are for context only, not display.
 */
export const formatKnowledgeCitations = (text: string): string =>
  text
    .replace(
      /\[source:\s*([^\]|]+?)(?:\s*§\s*(\d+))?\s*\]/gi,
      (_match, name: string, passage?: string) => {
        const label = passage
          ? `${name.trim()} §${passage}`
          : name.trim();
        return `**[${label}]**`;
      },
    )
    .replace(/\[context:\s*([^\]]+)\]/gi, (_match, name: string) => {
      return `**[${name.trim()}]**`;
    });

export const stripInternalTags = (text: string): string =>
  text.replace(/<tool_result_ids>[\s\S]*?<\/tool_result_ids>/g, '').trim();

export const extractAIMessageContent = (messages: any[]): string => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    if (typeof msg === 'string') {
      return stripInternalTags(msg);
    }

    if (typeof msg === 'object' && msg.id && Array.isArray(msg.id)) {
      const isAIMessage = msg.id[msg.id.length - 1] === 'AIMessage';
      if (isAIMessage && msg.kwargs) {
        const content = msg.kwargs.content || '';
        const hasToolCalls = msg.kwargs.tool_calls?.length > 0;

        if (content.trim() && !hasToolCalls) {
          return stripInternalTags(content);
        }
      }
    }
  }

  return '';
};

export const extractStreamContent = (chunk: any): string => {
  // Only extract content marked as final response (goes to text block)
  if (chunk?.type === 'content_stream' && chunk.content && chunk.is_final) {
    return chunk.content;
  }
  return '';
};

export const extractStreamingReasoningStep = (
  chunk: any,
): ReasoningStep | null => {
  if (chunk?.type === 'tool_log') {
    return {
      content: chunk.content || undefined,
      tools: [],
      type: 'tool_log',
    };
  }
  return null;
};

export const extractAllReasoningSteps = (messages: any[]): ReasoningStep[] => {
  const steps: ReasoningStep[] = [];

  const toolResults = new Map<
    string,
    { status: 'completed' | 'error'; description: string }
  >();

  for (const msg of messages) {
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType === 'ToolMessage' && msg.kwargs) {
      const toolCallId = msg.kwargs.tool_call_id;
      const status = msg.kwargs.status;
      const rawContent = msg.kwargs.content;
      const isError = status === 'error';

      let description = '';
      if (isError) {
        description = 'Failed';
      } else {
        try {
          const parsed =
            typeof rawContent === 'string'
              ? JSON.parse(rawContent)
              : rawContent;
          description = parsed?.description || '';
        } catch {
          description = '';
        }
      }

      toolResults.set(toolCallId, {
        status: isError ? 'error' : 'completed',
        description,
      });
    }
  }

  for (const msg of messages) {
    const msgType = msg?.id?.[msg.id.length - 1];
    if (msgType === 'AIMessage' && msg.kwargs) {
      const toolCalls = msg.kwargs.tool_calls || [];

      // Only extract tool info - content comes from streaming with is_reasoning flag
      // This prevents duplication between text block and reasoning steps
      if (toolCalls.length > 0) {
        const tools = toolCalls.map((toolCall: any) => {
          const callId = toolCall.id;
          const toolName = formatToolName(toolCall.name || '');
          const result = toolResults.get(callId);

          return {
            name: toolName,
            callId,
            status: result?.status || 'pending',
            description: result?.description,
          };
        });

        steps.push({
          content: undefined,
          tools: tools,
          type: 'reasoning',
        });
      }
    }

    if (typeof msg === 'object' && msg.type === 'tool_log') {
      steps.push({
        content: msg.content || undefined,
        tools: [],
        type: 'tool_log',
      });
    }
  }
  return steps;
};

export const formatToolName = (toolName: string): string => {
  return toolName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const renderTextWithDownloadLinks = (text: string) => {
  const parts = text.split(/(https:\/\/wlc360ai[^\s]*)/g);

  return parts.map((part, index) => {
    if (part.match(/^https:\/\/wlc360ai/)) {
      return { type: 'url' as const, content: part, key: index };
    }
    return { type: 'text' as const, content: part, key: index };
  });
};

/**
 * Creates a streaming handler for real-time updates during chat streaming
 */
export const createStreamingHandler = (
  streamingMessageIndex: number,
  requestId: string,
  isMountedRef: React.MutableRefObject<boolean>,
  setStreamingStatus: (status: string) => void,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
) => {
  return (
    content: string,
    extras?: ChatMessageType['extras'],
    reasoningSteps?: ReasoningStep[],
    transparencySummary?: ChatMessageType['transparencySummary'],
    skillsApplied?: ChatMessageType['skillsApplied'],
    contextApplied?: ChatMessageType['contextApplied'],
  ) => {
    if (!isMountedRef.current) return;

    let newStatus = '';

    if (content && content.trim().length > 0) {
      // Final content is being generated
      newStatus = 'Generating response...';
    } else if (reasoningSteps && reasoningSteps.length > 0) {
      // Check if any tools are pending
      const hasPendingTools = reasoningSteps.some(step =>
        step.tools?.some(tool => tool.status === 'pending'),
      );

      if (hasPendingTools) {
        newStatus = 'Running tools...';
      } else {
        newStatus = 'Thinking...';
      }
    } else {
      newStatus = 'Thinking...';
    }

    setStreamingStatus(newStatus);

    // Update message with streaming content
    setMessages(prev => {
      const updated = [...prev];
      const existing = updated[streamingMessageIndex];
      const newBlocks = updateBlocks(existing?.blocks, content, extras);
      const newReasoningSteps = updateReasoningSteps(
        existing?.reasoningSteps,
        reasoningSteps,
      );
      updated[streamingMessageIndex] = {
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
        request_id: requestId,
        blocks: newBlocks,
        reasoningSteps: newReasoningSteps,
        transparencySummary,
        skillsApplied,
        contextApplied,
        extras,
      } as any;
      return updated;
    });
  };
};

export const calculateReasoningStepsHash = (
  reasoningSteps: ReasoningStep[],
  lastReasoningStepsHash: string,
) => {
  const reasoningStepsHash = JSON.stringify(
    reasoningSteps.map(s => ({
      content: s.content?.substring(0, 100),
      tools: s.tools?.map(t => ({ name: t.name, status: t.status })),
    })),
  );
  const reasoningStepsChanged = reasoningStepsHash !== lastReasoningStepsHash;

  return {
    reasoningStepsHash,
    reasoningStepsChanged,
  };
};
